import React, { useState } from 'react';
import { Link, useNavigate, Outlet } from "react-router-dom";

import services from '../../data/services';
import i18n from '../../i18n';

const Services = () => {
  const serviceKeys = Object.keys(services);
  const [hoveredService, setHoveredService] = useState(null);
  const navigate = useNavigate();

  const filteredServices = serviceKeys;

  return (
    <div className="min-h-screen bg-warm-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-earth overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-10 w-24 h-24 bg-white rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="animate-fade-in-up">
            <span className="text-green-200 font-body font-semibold text-lg tracking-wide uppercase block mb-6">
              Healing Treatments
            </span>
            <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-bold mb-8">
              {i18n.services}
            </h1>
            <div className="mx-auto space-y-3 ">
              <p className="text-blue-100 text-xl md:text-2xl font-light">
                {i18n.service_subheader}
              </p>
              <div className="text-green-200 text-xl md:text-2xl font-medium">
                {i18n.service_subheader2}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredServices.map((key, index) => {
              const service = services[key];
              const isHovered = hoveredService === key;

              return (
                <div
                  key={key}
                  className="card-wellness service-card-hover group cursor-pointer animate-fade-in-up flex flex-col h-full"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredService(key)}
                  onMouseLeave={() => setHoveredService(null)}
                  onClick={(e) => {
                    if (!e.target.closest('a') && !e.target.closest('button')) {
                      navigate(`/services/${key}`);
                    }
                  }}
                >
                  {/* Image Section */}
                  <div className="relative overflow-hidden rounded-t-2xl">
                    <img
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                      src={service.cover}
                      alt={service.title}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-1">
                    <Link
                      to={`/services/${key}`}
                      className="no-underline text-inherit"
                    >
                      <h3 className="font-display text-2xl font-bold text-primary-dark mb-3 group-hover:text-primary transition-colors duration-200">
                        {service.title}
                      </h3>
                    </Link>
                    
                    <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-3">
                      {service.brief}
                    </p>

                    {/* Benefits Preview */}
                    <div className="mb-6 flex-1">
                      <h4 className="text-sm font-semibold text-primary mb-3 flex items-center">
                        Key Benefits
                      </h4>
                      <ul className="space-y-2">
                        {service.benefits.slice(0, 3).map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="text-xs text-gray-600 flex items-start">
                            <span className="text-primary mr-2 text-sm">✓</span>
                            <span className="flex-1">{benefit}</span>
                          </li>
                        ))}
                        {service.benefits.length > 3 && (
                          <li className="text-xs text-primary font-medium ml-6">
                            +{service.benefits.length - 3} more benefits
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3 mt-auto">
                      <a
                        href="https://www.fresha.com/providers/swami-ayurveda-ia5mxbwa?allOffer=true"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-block w-full text-center py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg no-underline"
                      >
                        Book This Treatment
                      </a>
                      <Link
                        to={`/services/${key}`}
                        className="btn-secondary w-full text-center py-3 rounded-xl font-semibold no-underline flex items-center justify-center space-x-2"
                      >
                        <span>Learn More</span>
                      </Link>
                    </div>
                  </div>

                  {/* Hover Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent transition-opacity duration-300 pointer-events-none rounded-2xl ${isHovered ? 'opacity-100' : 'opacity-0'}`}></div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-primary py-16 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="animate-fade-in-up">
            <h2 className="font-display text-white text-3xl md:text-4xl font-bold mb-6">
              Ready to Begin Your Healing Journey?
            </h2>
            <p className="text-blue-100 text-lg mb-8 leading-relaxed text-center">
              Experience the transformative power of Ayurveda with personalized treatments designed to restore balance and vitality to your life.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://www.fresha.com/providers/swami-ayurveda-ia5mxbwa?allOffer=true"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold text-lg px-8 py-4 rounded-full font-display transform hover:scale-105 shadow-lg"
              >
                Schedule Your Consultation
              </a>
              <Link
                to="/about"
                className="btn-secondary text-lg px-8 py-4 rounded-full"
              >
                Meet Your Practitioner
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Outlet />
    </div>
  );
};

export default Services;
